// 모든 import 문을 먼저 작성
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMembership } from '../../hooks/MembershipProvider/MembershipProvider';
import './Admin.css';

// 개별 컴포넌트들을 import (순환 import 방지)
import Dashboard from './Dashboard/Dashboard';
import Users from './Users/Users';
import Orders from './Orders/Orders';
import Products from './Products/Products';
import Analytics from './Analytics/Analytics';
import Settings from './Settings/Settings';
import UserDetail from './Users/UserDetail';
import OrderDetail from './Orders/OrderDetail';
import OrderEdit from './Orders/OrderEdit';
import OrderRefund from './Orders/OrderRefund';
import Delivery from './Delivery/Delivery';
import DeliveryDetail from './Delivery/DeliveryDetail';
import InterviewDetail from './Users/InterviewDetail';
import DigitalbookDetail from './Users/DigitalbookDetail';
import Coupons from './Coupon/Coupons';
import CouponDetail from './Coupon/CouponDetail';
import CouponEdit from './Coupon/CouponEdit';
import CouponCreate from './Coupon/CouponCreate';

// Admin 컴포넌트들을 모두 익스포트
export { Dashboard } from './Dashboard';
export { Users } from './Users';
export { Orders } from './Orders';
export { Products } from './Products';
export { Analytics } from './Analytics';
export { Settings } from './Settings';
export { UserDetail } from './Users';
export { OrderDetail } from './Orders';
export { default as Delivery } from './Delivery/Delivery';
export { default as DeliveryDetail } from './Delivery/DeliveryDetail';
export { default as InterviewDetail } from './Users/InterviewDetail';
export { default as DigitalbookDetail } from './Users/DigitalbookDetail';
export { default as Coupons } from './Coupon/Coupons';
export { default as CouponDetail } from './Coupon/CouponDetail';
export { default as CouponEdit } from './Coupon/CouponEdit';
export { default as CouponCreate } from './Coupon/CouponCreate';
// 기본 Admin 컴포넌트 (관리자 페이지의 메인 레이아웃)

interface AdminProps {
  currentPage?: 'dashboard' | 'users' | 'user-detail' | 'interview-detail' | 'digitalbook-detail' | 'orders' | 'order-detail' | 'order-edit' | 'order-refund' | 'products' | 'analytics' | 'settings' | 'delivery' | 'delivery-detail' | 'coupons' | 'coupon-detail' | 'coupon-edit' | 'coupon-create';
}

const Admin: React.FC<AdminProps> = ({ currentPage = 'dashboard' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    hasAdminPermission, 
    hasManagerPermission, 
    hasSystemPermission 
  } = useMembership();
  
  // 사이드바 펼침/접힘 상태
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  
  // URL 경로에 따라 현재 페이지 결정
  const getCurrentPageFromPath = () => {
    const path = location.pathname;
    
    // 정확한 패턴 매칭을 위해 정규식 사용
    if (path.includes('/users/detail/')) return 'user-detail';
    
    // 인터뷰 상세 페이지 패턴: /admin/users/:userId/projects/:projectId/episodes/:episodeId/interview
    if (/^\/admin\/users\/[^\/]+\/projects\/[^\/]+\/episodes\/[^\/]+\/interview$/.test(path)) {
      return 'interview-detail';  
    }

    // 전자책 상세 페이지 패턴: /admin/users/:userId/projects/:projectId/books/:bookId
    if (/^\/admin\/users\/[^\/]+\/projects\/[^\/]+\/books\/[^\/]+$/.test(path)) {
      return 'digitalbook-detail';
    }
    
    if (path.includes('/orders/refund/')) return 'order-refund';
    if (path.includes('/orders/edit/')) return 'order-edit';
    if (path.includes('/orders/detail/')) return 'order-detail';
    if (path.includes('/delivery/detail/')) return 'delivery-detail';
    if (path.includes('/coupon/create')) return 'coupon-create';
    if (path.includes('/coupon/edit/')) return 'coupon-edit';
    if (path.match(/\/coupon\/[^\/]+$/)) return 'coupon-detail';
    if (path.includes('/users')) return 'users';
    if (path.includes('/orders')) return 'orders';
    if (path.includes('/delivery')) return 'delivery';
    if (path.includes('/coupon')) return 'coupons';
    if (path.includes('/products')) return 'products';
    if (path.includes('/analytics')) return 'analytics';
    if (path.includes('/settings')) return 'settings';

    return 'dashboard';
  };
  
  const activePage = getCurrentPageFromPath();
  
  const renderCurrentPage = () => {
    switch (activePage) {
      case 'dashboard':
        return <Dashboard />;
      case 'users':
        return <Users />;
      case 'user-detail':
        return <UserDetail />;
      case 'interview-detail':
        return <InterviewDetail />;
      case 'digitalbook-detail':
        return <DigitalbookDetail />;
      case 'orders':
        return <Orders />;
      case 'order-detail':
        return <OrderDetail />;
      case 'order-edit':
        return <OrderEdit />;
      case 'order-refund':
        return <OrderRefund />;
      case 'delivery':
        return <Delivery />;
      case 'delivery-detail':
        return <DeliveryDetail />;
      case 'coupons':
        return <Coupons />;
      case 'coupon-detail':
        return <CouponDetail />;
      case 'coupon-edit':
        return <CouponEdit />;
      case 'coupon-create':
        return <CouponCreate />;
      case 'products':
        return <Products />;
      case 'analytics':
        return <Analytics />;
      case 'settings':
        return <Settings />;
      default:
        return <Dashboard />;
    }
  };
  
  return (
    <div className={`admin-container ${isSidebarExpanded ? 'sidebar-expanded' : 'sidebar-collapsed'}`}>
      <div className={`admin-sidebar ${isSidebarExpanded ? 'expanded' : 'collapsed'}`}>
        <h2 className={`admin-title ${isSidebarExpanded ? 'expanded' : 'collapsed'}`}>
          {isSidebarExpanded ? '관리자 패널' : '⚙️'}
        </h2>

        <nav className={`admin-nav ${isSidebarExpanded ? 'expanded' : 'collapsed'}`}>
          {/* <button 
            onClick={() => navigate('/admin')} 
            className={`nav-item ${activePage === 'dashboard' ? 'active' : ''}`}
            title="대시보드"
          >
            {isSidebarExpanded ? '📊 대시보드' : '📊'}
          </button> */}
          
          {/* 관리자 권한이 있는 경우에만 사용자 관리 메뉴 표시 */}
          {hasAdminPermission() && (
            <button 
              onClick={() => navigate('/admin/users')} 
              className={`nav-item ${activePage === 'users' || activePage === 'user-detail' || activePage === 'interview-detail' || activePage === 'digitalbook-detail' ? 'active' : ''}`}
              title="사용자 관리"
            >
              {isSidebarExpanded ? '👥 사용자 관리' : '👥'}
            </button>
          )}
          
          {/* 매니저 이상 권한이 있는 경우에만 주문/상품 관리 메뉴 표시 */}
          {(hasManagerPermission() || hasAdminPermission()) && (
            <>
              <button
                onClick={() => navigate('/admin/orders')} 
                className={`nav-item ${activePage === 'orders' ? 'active' : ''}`}
                title="결제 관리"
              >
                {isSidebarExpanded ? '📦 결제 관리' : '📦'}
              </button>
              <button 
                onClick={() => navigate('/admin/delivery')} 
                className={`nav-item ${activePage === 'delivery' || activePage === 'delivery-detail' ? 'active' : ''}`}
                title="실물책 주문관리"
              >
                {isSidebarExpanded ? '📚 실물책 주문관리' : '📚'}
              </button>
              <button 
                onClick={() => navigate('/admin/coupon')} 
                className={`nav-item ${activePage === 'coupons' || activePage === 'coupon-detail' || activePage === 'coupon-edit' || activePage === 'coupon-create' ? 'active' : ''}`}
                title="쿠폰 관리"
              >
                {isSidebarExpanded ? '🎫 쿠폰 관리' : '🎫'}
              </button>
              {/* <button 
                onClick={() => navigate('/admin/products')} 
                className={`nav-item ${activePage === 'products' ? 'active' : ''}`}
                title="상품 관리"
              >
                {isSidebarExpanded ? '🛍️ 상품 관리' : '🛍️'}
              </button> */}
            </>
          )}
          
          {/* 관리자 권한이 있는 경우에만 통계 분석 메뉴 표시 */}
          {hasAdminPermission() && (
            <>
              {/* <button 
                onClick={() => navigate('/admin/analytics')} 
                className={`nav-item ${activePage === 'analytics' ? 'active' : ''}`}
                title="통계 분석"
              >
                {isSidebarExpanded ? '📈 통계 분석' : '📈'}
              </button> */}
            </>
          )}
          
          {/* 시스템 권한이 있는 경우에만 설정 메뉴 표시 */}
          {/* {hasSystemPermission() && (
            <button 
              onClick={() => navigate('/admin/settings')} 
              className={`nav-item ${activePage === 'settings' ? 'active' : ''}`}
              title="시스템 설정"
            >
              {isSidebarExpanded ? '⚙️ 시스템 설정' : '⚙️'}
            </button>
          )} */}
          
          {/* 사이드바 토글 버튼 */}
          <div className="sidebar-toggle-container">
            <button 
              className="sidebar-toggle-btn"
              onClick={() => setIsSidebarExpanded(!isSidebarExpanded)}
              title={isSidebarExpanded ? "메뉴 접기" : "메뉴 펼치기"}
            >
              {isSidebarExpanded ? (
                <svg className="toggle-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              ) : (
                <svg className="toggle-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              )}
            </button>
          </div>
        </nav>
      </div>
      <div className={`admin-content ${isSidebarExpanded ? 'sidebar-expanded' : 'sidebar-collapsed'}`}>
        <div className={`admin-content-inner`}>
          {renderCurrentPage()}
        </div>
      </div>
    </div>
  );
};

export default Admin;
